import Box from '@material-ui/core/Box';
import EventBus from 'eventing-bus';
import * as livekit from 'livekit-client';
import Typography from '@material-ui/core/Typography';
import TextField from '@mui/material/TextField';
import { useStopwatch } from 'react-timer-hook';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Select, MenuItem, FormControl } from '@mui/material';

import '../../Agent/index.css';
import constants from '../../../constants';
import Functions from '../../Agent/SingleAgent/Functions';
import TopSettings from '../../Agent/SingleAgent/TopSettings';
import MicSettings from '../../Agent/SingleAgent/MicSettings';
import useMicDevices from '../../../utils/useMicDevices';
import { getSingleAgent, updateAgent, getConnectionDetails, getVoiceAgentStatus } from '../../../store/Agent';

// CampaignAgentEditor — embeds the agent editor (Prompt / Settings / Test tabs) inside the campaign
// Agent Call node. Reuses the agent page's prop-driven sub-components (TopSettings, Functions,
// MicSettings) and replicates the agentData load/save + LiveKit test logic. All edits persist to the
// underlying Agent document via updateAgent, so changes here and on the agent page stay in sync.
const CampaignAgentEditor = ({ agentId, defaultTab = 'prompt', extraPromptContent = null, onScriptSaved = null }) => {

    const dispatch = useDispatch();
    const { stats, singleAgent, connectionDetails, isLoading, voiceAgentStatus } = useSelector((state) => state['Agent']);

    const [activeTab, setActiveTab] = useState(defaultTab);
    const [isUpdated, setUpdated] = useState(false);
    const [agentData, setAgentData] = useState({});
    const [isEdited, setIsEdited] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [roomInstance, setRoomInstance] = useState(null);
    const [isAgentConnected, setIsAgentConnected] = useState(false);
    const [ecsWarmingUp, setEcsWarmingUp] = useState(false);
    const [speakingState, setSpeakingState] = useState('Connecting');

    const [openWelcome, setOpenWelcome] = useState(false);
    const [searchWelcome, setSearchWelcome] = useState('');

    const { seconds, minutes, isRunning, start, reset } = useStopwatch({ autoStart: false });
    const formattedSeconds = String(seconds).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');

    const { micState, selectedIdRef, micUserSetRef, stopMeter, handleMicChange, handleSpeakerChange, registerAudioElement, clearAudioElements, toggleMicPanel } = useMicDevices();

    // ========== Load agent on mount / agent change ==========
    // getSingleAgent populates voices, allModels, ttsProviders AND singleAgent in one shot — TopSettings reads them.
    useEffect(() => {
        dispatch({ type: 'CLEAR_CONNECTION_DETAILS' });
        if (agentId) dispatch(getSingleAgent(agentId));
    }, [agentId]);

    useEffect(() => {
        if (singleAgent && singleAgent['_id'] === agentId) setAgentData(singleAgent);
    }, [singleAgent, agentId]);

    // Mirror the agent's script onto the campaign node config — the backend builds the outbound call's
    // callScript from node.config.script (server/api/campaign/campaign.helper.js), so keep it in sync.
    useEffect(() => {
        if (onScriptSaved && agentData?.['_id'] === agentId && agentData?.['script'] !== undefined) onScriptSaved(agentData['script']);
    }, [agentData?.['script'], agentId]);

    useEffect(() => { if (isUpdated) handleUpdateAgent() }, [agentData?.['language'], agentData?.['modelType'], agentData?.['webhookURL'], agentData?.['dynamicVariables'], agentData?.['welcomeMessageType'], agentData?.['llmTemperature']]);
    useEffect(() => { if (connectionDetails && isAgentConnected) initializeAgent(connectionDetails) }, [connectionDetails]);

    // ========== Initialize LiveKit room ==========
    const initializeAgent = async (connectionDetails) => {
        if (!connectionDetails) return console.error('No connection details available');
        try {
            const { serverUrl, participantToken } = connectionDetails;
            const room = new livekit.Room();
            await room.connect(serverUrl, participantToken);
            await room.localParticipant.setMicrophoneEnabled(true);

            // Only switch the input device if the user actively picked a specific mic. Forcing
            // switchActiveDevice for the OS default flips AirPods into hands-free mode and kills output audio.
            if (micUserSetRef.current && selectedIdRef.current) {
                try { await room.switchActiveDevice('audioinput', selectedIdRef.current); }
                catch (error) { console.error('Error applying selected mic device:', error); }
            }

            start();
            room.on('activeSpeakersChanged', (speakers) => { handleActiveSpeakerChange(speakers) });
            room.on('trackSubscribed', (track) => {
                if (track.kind === 'audio') {
                    const audioElement = track.attach();
                    document.body.appendChild(audioElement);
                    registerAudioElement(audioElement);
                }
            });
            room.on(livekit.RoomEvent.Disconnected, () => { resetCallUI(); });

            setRoomInstance(room);
            setIsAgentConnected(true);
        } catch (error) {
            console.error('Error connecting to LiveKit:', error);
            setIsAgentConnected(false);
        }
    }

    const handleActiveSpeakerChange = (speakers) => {
        speakers.forEach(speaker => {
            const isAgent = speaker.identity.includes('voice_assistant');
            if (isAgent) setSpeakingState('Listening');
            else setSpeakingState('Speaking');
        });
        if (speakers.length === 0) setSpeakingState('Thinking');
    }

    // ========== Start / poll / disconnect ==========
    const startAgent = () => {
        dispatch({ type: 'SET_VOICE_AGENT_STATUS', payload: null });
        dispatch(getVoiceAgentStatus());
    }

    useEffect(() => {
        if (!voiceAgentStatus) return;
        if (voiceAgentStatus === 'warming_up') setEcsWarmingUp(true);
        else if (voiceAgentStatus === 'ready') {
            if (ecsWarmingUp) setEcsWarmingUp(false);
            else {
                setIsAgentConnected(true);
                dispatch(getConnectionDetails({ agentId: agentData['_id'], callType: 'voiceagent-call' }));
                setRoomInstance(null);
            }
        }
    }, [voiceAgentStatus]);

    useEffect(() => {
        if (!ecsWarmingUp) return;
        const interval = setInterval(() => dispatch(getVoiceAgentStatus()), 15000);
        return () => clearInterval(interval);
    }, [ecsWarmingUp]);

    const resetCallUI = () => {
        setRoomInstance(null);
        reset(new Date(), false);
        setIsAgentConnected(false);
        setSpeakingState('Connecting');
        stopMeter();
        clearAudioElements();
    }

    const disconnectAgent = () => {
        if (roomInstance) {
            roomInstance.disconnect();
            resetCallUI();
        }
    }

    // ========== Cleanup: end any live call when leaving Test tab, switching agent, or unmounting ==========
    // initializeAgent appends <audio> to document.body — must tear those down to avoid orphaned playback.
    useEffect(() => {
        if (activeTab !== 'test' && isAgentConnected) disconnectAgent();
    }, [activeTab]);

    useEffect(() => () => { if (roomInstance) roomInstance.disconnect(); clearAudioElements(); }, [roomInstance]);

    // ========== Update Agent (saves to the Agent document) ==========
    const handleUpdateAgent = (file = selectedFile) => {
        setUpdated(false);
        const formData = new FormData();
        const agentKeys = ['name', 'script', 'firstMessage', 'welcomeMessageType', 'modelType', 'language', 'webhookURL', 'dynamicVariables', 'llmTemperature'];
        for (const key of agentKeys) if (agentData[key] !== undefined)
            if (agentData[key] && typeof agentData[key] === 'object') formData.append(key, JSON.stringify(agentData[key]));
            else formData.append(key, agentData[key]);

        if (agentData['webhookURL'] === '') formData.append('webhookURL', '');

        formData.append('voiceId', agentData['voiceId']?.['_id'] || '');
        formData.append('modelId', agentData['modelId']?.['_id'] || '');

        if (file) formData.append('file', file);

        dispatch(updateAgent(agentId, formData, () => setSelectedFile(null), () => console.log('Update failed')));
    }

    // ========== Welcome message ==========
    const filteredWelcomeMessage = constants['welcomeMessage'].filter((status) => status['label'].toLowerCase().includes(searchWelcome.toLowerCase()));
    const handleWelcomeMessage = (value) => {
        setSearchWelcome('');
        setOpenWelcome(false);
        const selectedOption = constants['welcomeMessage'].find((option) => option['label'] === value);
        if (selectedOption) {
            setAgentData({ ...agentData, welcomeMessageType: selectedOption['value'] });
            setUpdated(true);
        }
    };

    const renderMicSettings = (children) => (<MicSettings micState={micState} roomInstance={roomInstance} toggleMicPanel={toggleMicPanel} handleMicChange={handleMicChange} handleSpeakerChange={handleSpeakerChange}>{children}</MicSettings>);

    // ========== Cost / latency status classes (mirrors the agent page header) ==========
    const costTotal = parseFloat(stats?.['cost']?.['total'] || 0);
    let costClass = 'green-bg';
    if (costTotal > 0.075) costClass = 'orange-bg';
    else if (costTotal > 0.1) costClass = 'red-bg';

    const latencyTotal = parseFloat(stats?.['latency']?.['total'] || 0);
    let latencyClass = 'green-bg';
    if (latencyTotal > 1000) latencyClass = 'red-bg';
    else if (latencyTotal > 750) latencyClass = 'orange-bg';

    return (
        <div className='campaign-agent-editor'>
            {/* Agent meta line (Single prompt · Agent ID · cost/min · Estimated Latency) — mirrors the agent page header */}
            <div className='campaign-agent-editor-meta'>
                <p>Single prompt</p>
                <ul>
                    <li className=''>
                        <h4>Agent ID: {agentData?.['_id'] ? `${agentData['_id'].slice(0, 4)}.....${agentData['_id'].slice(-4)}` : 'Loading...'}
                            <CopyToClipboard text={agentData?.['_id']} onCopy={() => EventBus.publish('success', 'Address Copied!')}>
                                <span className='copy-btn'>
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M5.55 11.375H3.45C1.495 11.375 0.625 10.505 0.625 8.55V6.45C0.625 4.495 1.495 3.625 3.45 3.625H5.55C7.505 3.625 8.375 4.495 8.375 6.45V8.55C8.375 10.505 7.505 11.375 5.55 11.375ZM3.45 4.375C1.9 4.375 1.375 4.9 1.375 6.45V8.55C1.375 10.1 1.9 10.625 3.45 10.625H5.55C7.1 10.625 7.625 10.1 7.625 8.55V6.45C7.625 4.9 7.1 4.375 5.55 4.375H3.45Z" fill="#4B5563" />
                                        <path d="M8.55 8.375H8C7.795 8.375 7.625 8.205 7.625 8V6.45C7.625 4.9 7.1 4.375 5.55 4.375H4C3.795 4.375 3.625 4.205 3.625 4V3.45C3.625 1.495 4.495 0.625 6.45 0.625H8.55C10.505 0.625 11.375 1.495 11.375 3.45V5.55C11.375 7.505 10.505 8.375 8.55 8.375ZM8.375 7.625H8.55C10.1 7.625 10.625 7.1 10.625 5.55V3.45C10.625 1.9 10.1 1.375 8.55 1.375H6.45C4.9 1.375 4.375 1.9 4.375 3.45V3.625H5.55C7.505 3.625 8.375 4.495 8.375 6.45V7.625Z" fill="#4B5563" />
                                    </svg>
                                </span>
                            </CopyToClipboard>
                        </h4>
                    </li>
                    <li className={costClass}>
                        <h4>${parseFloat(costTotal).toFixed(2) || 0}/min
                            <i className='icon'>
                                <span className=''>
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 0.625C3.035 0.625 0.625 3.035 0.625 6C0.625 8.965 3.035 11.375 6 11.375C8.965 11.375 11.375 8.965 11.375 6C11.375 3.035 8.965 0.625 6 0.625ZM6 10.625C3.45 10.625 1.375 8.55 1.375 6C1.375 3.45 3.45 1.375 6 1.375C8.55 1.375 10.625 3.45 10.625 6C10.625 8.55 8.55 10.625 6 10.625Z" fill="#4B5563" />
                                        <path d="M6 5.125C5.795 5.125 5.625 5.295 5.625 5.5V8C5.625 8.205 5.795 8.375 6 8.375C6.205 8.375 6.375 8.205 6.375 8V5.5C6.375 5.295 6.205 5.125 6 5.125Z" fill="#4B5563" />
                                    </svg>
                                </span>
                                <div className='overlay-box'>
                                    <div className='inner-box'>
                                        <div className='tim-box'><h4>STT</h4><p>${parseFloat(stats?.['cost']?.['stt'] || 0).toFixed(2)}/min</p></div>
                                        <div className='tim-box'><h4>LLM</h4><p>${parseFloat(stats?.['cost']?.['llm'] || 0).toFixed(2)}/min</p></div>
                                        <div className='tim-box'><h4>TTS</h4><p>${parseFloat(stats?.['cost']?.['tts'] || 0).toFixed(2)}/min</p></div>
                                        <div className='tim-box'><h4>Total</h4><p>${parseFloat(stats?.['cost']?.['total'] || 0).toFixed(2)}/min</p></div>
                                    </div>
                                </div>
                            </i>
                        </h4>
                    </li>
                    <li className={latencyClass}>
                        <h4>Estimated Latency: {latencyTotal || 0}ms
                            <i className='icon'>
                                <span className=''>
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 0.625C3.035 0.625 0.625 3.035 0.625 6C0.625 8.965 3.035 11.375 6 11.375C8.965 11.375 11.375 8.965 11.375 6C11.375 3.035 8.965 0.625 6 0.625ZM6 10.625C3.45 10.625 1.375 8.55 1.375 6C1.375 3.45 3.45 1.375 6 1.375C8.55 1.375 10.625 3.45 10.625 6C10.625 8.55 8.55 10.625 6 10.625Z" fill="#4B5563" />
                                        <path d="M6 5.125C5.795 5.125 5.625 5.295 5.625 5.5V8C5.625 8.205 5.795 8.375 6 8.375C6.205 8.375 6.375 8.205 6.375 8V5.5C6.375 5.295 6.205 5.125 6 5.125Z" fill="#4B5563" />
                                    </svg>
                                </span>
                                <div className='overlay-box'>
                                    <div className='inner-box'>
                                        <div className='tim-box'><h4>STT</h4><p>{stats?.['latency']?.['stt'] || 0}ms</p></div>
                                        <div className='tim-box'><h4>LLM</h4><p>{stats?.['latency']?.['llm'] || 0}ms</p></div>
                                        <div className='tim-box'><h4>TTS</h4><p>{stats?.['latency']?.['tts'] || 0}ms</p></div>
                                        <div className='tim-box'><h4>Total</h4><p>{stats?.['latency']?.['total'] || 0}ms</p></div>
                                    </div>
                                </div>
                            </i>
                        </h4>
                    </li>
                </ul>
            </div>
            <div className='campaign-agent-editor-tabs'>
                <button type='button' className={`campaign-agent-editor-tab ${activeTab === 'prompt' ? 'active' : ''}`} onClick={() => setActiveTab('prompt')}>Prompt</button>
                <button type='button' className={`campaign-agent-editor-tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>Settings</button>
                <button type='button' className={`campaign-agent-editor-tab ${activeTab === 'test' ? 'active' : ''}`} onClick={() => setActiveTab('test')}>Test</button>
            </div>

            {/* Reuse the agent page's CSS by reproducing its ancestor class chain (.bottom-content-area > .agent-detail-bottom). */}
            <div className='campaign-agent-editor-body bottom-content-area'>
              <div className='agent-detail-bottom'>
                {/* ========== Prompt Tab ========== */}
                {activeTab === 'prompt' && (
                    <div className='agent-detail-text'>
                        <div className='agent-detail-box'>
                            <TopSettings agentId={agentId} agentData={agentData} setAgentData={setAgentData} setUpdated={setUpdated} />
                            <div
                                key={agentId}
                                contentEditable
                                suppressContentEditableWarning
                                onBlur={(e) => {
                                    const updatedScript = e['target']['innerHTML'].trim() || ' ';
                                    setAgentData((prev) => {
                                        if (prev['script'] !== updatedScript) setIsEdited(true);
                                        return { ...prev, script: updatedScript };
                                    })
                                }}
                                onInput={() => setIsEdited(true)}
                                dangerouslySetInnerHTML={{ __html: agentData?.['script'] || '' }}
                            />
                            {isEdited && (<button className='btn-style-new' onClick={() => { handleUpdateAgent(); setIsEdited(false); }}> Save </button>)}
                        </div>

                        <div className='welcome-msg'>
                            <h4>Welcome Message</h4>
                            <div className='group-form'>
                                <FormControl fullWidth>
                                    <Select
                                        displayEmpty
                                        open={openWelcome}
                                        value={agentData?.['welcomeMessageType'] ? constants['welcomeMessage'].find(opt => opt['value'] === agentData['welcomeMessageType'])?.['label'] : 'AI Initiates: AI begins with your defined begin message.'}
                                        onOpen={() => setOpenWelcome(true)}
                                        onClose={() => setOpenWelcome(false)}
                                        onChange={(e) => handleWelcomeMessage(e['target']['value'])}
                                        renderValue={(selected) => {
                                            if (!selected || selected === 'AI Initiates: AI begins with your defined begin message.') {
                                                return 'AI Initiates: AI begins with your defined begin message.';
                                            }
                                            const selectedOption = constants['welcomeMessage'].find((option) => option['label'] === selected);
                                            return selectedOption ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <i style={{ marginRight: '10px' }}>{selectedOption['icon']}</i>
                                                    <Typography sx={{ fontSize: '14px' }}>
                                                        <p>{selectedOption['label']} <span>{selectedOption['detail']}</span></p>
                                                    </Typography>
                                                </Box>
                                            ) : null;
                                        }}
                                        MenuProps={{ disableAutoFocusItem: true }}
                                    >
                                        {filteredWelcomeMessage.length > 0 ? (
                                            filteredWelcomeMessage.map((status) => (
                                                <MenuItem key={status['label']} value={status['label']} onClick={() => handleWelcomeMessage(status['label'])}>
                                                    <i style={{ marginRight: '10px' }}>{status['icon']}</i> <p>{status['label']} <span>{status['detail']}</span></p>
                                                </MenuItem>
                                            ))
                                        ) : (<MenuItem disabled>No results found</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </div>

                            {agentData?.['welcomeMessageType'] !== 'dynamic' && agentData?.['welcomeMessageType'] !== 'silent' && (
                                <div className='group-form mb-0'>
                                    <TextField
                                        fullWidth
                                        hiddenLabel
                                        size='small'
                                        placeholder=''
                                        variant='standard'
                                        name='firstMessage'
                                        onBlur={() => handleUpdateAgent()}
                                        value={agentData?.['firstMessage'] || ''}
                                        onChange={(e) => setAgentData({ ...agentData, firstMessage: e['target']['value'] })}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Campaign-specific Dynamic Variables (injected from SequenceStep) */}
                        {extraPromptContent}
                    </div>
                )}

                {/* ========== Settings Tab ========== */}
                {activeTab === 'settings' && (
                    <div className='campaign-agent-editor-settings'>
                        <Functions
                            agentId={agentId}
                            agentData={agentData}
                            setAgentData={setAgentData}
                            handleUpdateAgent={handleUpdateAgent}
                            setSelectedFile={setSelectedFile}
                        />
                    </div>
                )}

                {/* ========== Test Tab ========== */}
                {activeTab === 'test' && (
                    <div className='talk-speach-area'>
                        <div className='tabs-box'>
                            <div className='tabs-bottom'>
                                <div className='talk-area'>
                                    {!isAgentConnected ? (
                                        <div className='talk-box'>
                                            <i className='icon'>
                                                <svg width='56' height='56' viewBox='0 0 56 56' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                                    <path d='M28 2.33301C26.1435 2.33301 24.363 3.07051 23.0503 4.38326C21.7375 5.69602 21 7.47649 21 9.33301V27.9997C21 29.8562 21.7375 31.6367 23.0503 32.9494C24.363 34.2622 26.1435 34.9997 28 34.9997C29.8565 34.9997 31.637 34.2622 32.9497 32.9494C34.2625 31.6367 35 29.8562 35 27.9997V9.33301C35 7.47649 34.2625 5.69602 32.9497 4.38326C31.637 3.07051 29.8565 2.33301 28 2.33301Z' stroke='#F2F2F2' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                                                    <path d='M44.3332 23.333V27.9997C44.3332 32.3315 42.6123 36.486 39.5492 39.5491C36.4862 42.6122 32.3317 44.333 27.9998 44.333C23.668 44.333 19.5135 42.6122 16.4504 39.5491C13.3873 36.486 11.6665 32.3315 11.6665 27.9997V23.333' stroke='#F2F2F2' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                                                    <path d='M28 44.333V53.6663' stroke='#F2F2F2' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                                                    <path d='M18.6665 53.667H37.3332' stroke='#F2F2F2' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                                                </svg>
                                            </i>
                                            <p>{agentData?.['status'] === 'inactive' ? 'Agent is deactivated' : isLoading ? 'Loading agent...' : 'Test your agent'}</p>
                                            {renderMicSettings(
                                                <button
                                                    className='btn-style-border'
                                                    onClick={() => {
                                                        if (!agentData?.['_id']) { EventBus.publish('error', 'Please wait for agent data to load.'); return; }
                                                        if (agentData?.['status'] === 'inactive') { EventBus.publish('error', 'This agent has been deactivated. Please contact your support team to reactivate it.'); return; }
                                                        startAgent();
                                                    }}
                                                    disabled={isLoading || !agentData?.['_id'] || agentData?.['status'] === 'inactive' || ecsWarmingUp}
                                                    style={(isLoading || !agentData?.['_id'] || agentData?.['status'] === 'inactive' || ecsWarmingUp) ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                                >
                                                    {isLoading ? 'Loading...' : agentData?.['status'] === 'inactive' ? 'Deactivated' : 'Test'}
                                                </button>
                                            )}
                                            {ecsWarmingUp && (
                                                <div className='ecs-wakeup-dots'>
                                                    <div className='ecs-dots-row'>
                                                        <span className='ecs-dot'></span><span className='ecs-dot'></span><span className='ecs-dot'></span>
                                                        <span className='ecs-dot ecs-dot-center'></span>
                                                        <span className='ecs-dot'></span><span className='ecs-dot'></span><span className='ecs-dot'></span>
                                                    </div>
                                                    <p className='ecs-wakeup-text'>Agent is waking up, please wait 5–6 minutes...</p>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className='talk-box style-two'>
                                            <i className='icon'>
                                                <svg width='56' height='56' viewBox='0 0 56 56' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                                    <path d='M28 2.33301C26.1435 2.33301 24.363 3.07051 23.0503 4.38326C21.7375 5.69602 21 7.47649 21 9.33301V27.9997C21 29.8562 21.7375 31.6367 23.0503 32.9494C24.363 34.2622 26.1435 34.9997 28 34.9997C29.8565 34.9997 31.637 34.2622 32.9497 32.9494C34.2625 31.6367 35 29.8562 35 27.9997V9.33301C35 7.47649 34.2625 5.69602 32.9497 4.38326C31.637 3.07051 29.8565 2.33301 28 2.33301Z' stroke='#F2F2F2' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                                                    <path d='M44.3332 23.333V27.9997C44.3332 32.3315 42.6123 36.486 39.5492 39.5491C36.4862 42.6122 32.3317 44.333 27.9998 44.333C23.668 44.333 19.5135 42.6122 16.4504 39.5491C13.3873 36.486 11.6665 32.3315 11.6665 27.9997V23.333' stroke='#F2F2F2' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                                                    <path d='M28 44.333V53.6663' stroke='#F2F2F2' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                                                    <path d='M18.6665 53.667H37.3332' stroke='#F2F2F2' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                                                </svg>
                                            </i>
                                            <div className={`state-${speakingState}`}>
                                                <div className='state-dot'></div>
                                                <span className='state-text'>{speakingState.charAt(0).toUpperCase() + speakingState.slice(1)}</span>
                                                <div className='state-wave'></div>
                                            </div>
                                            {isRunning && <div className='timer-agent'> <p><span>{formattedMinutes}</span> : <span>{formattedSeconds}</span></p> </div>}
                                            {renderMicSettings(
                                                <button className='btn-style-red' onClick={() => { disconnectAgent() }}>
                                                    <i className='icon-end-call'>
                                                        <svg width='800px' height='800px' viewBox='0 0 24 24' version='1.1' xmlns='http://www.w3.org/2000/svg'>
                                                            <g stroke='none' strokeWidth='1' fill='none' fillRule='evenodd'>
                                                                <g fill='#212121' fillRule='nonzero'>
                                                                    <path d='M9.36703096,3.31256396 L10.2267719,5.33990935 C10.6014206,6.22336423 10.3935244,7.26227205 9.71279082,7.9083973 L7.81847309,9.70640851 C7.93535507,10.7818874 8.29686524,11.8408843 8.90300361,12.8833992 C9.50914197,13.9259142 10.266164,14.7907132 11.1740696,15.4777964 L13.4492636,14.7191114 C14.3116668,14.4315348 15.2508597,14.7620211 15.7798528,15.5392067 L17.0122122,17.349763 C17.6271206,18.2531736 17.516541,19.4995134 16.7535143,20.2655387 L15.9357982,21.0864601 C15.1218751,21.9035819 13.9593315,22.1999669 12.8839268,21.8645204 C10.3450716,21.0725857 8.0107575,18.7213775 5.88098445,14.8108958 C3.74811387,10.8947267 2.9953647,7.57213801 3.62273695,4.84312972 C3.88673284,3.6948166 4.70424816,2.78033469 5.77175058,2.43923166 L6.84834294,2.09522409 C7.85716063,1.77287281 8.93501107,2.29382273 9.36703096,3.31256396 Z M14.4978449,8.43869174 L20.7184544,2.21549812 C21.0112723,1.92252955 21.486146,1.92240737 21.7791146,2.21522523 C22.0454496,2.48142327 22.0697629,2.89808071 21.851984,3.19174825 L21.7793874,3.27588536 L15.5588449,9.49869174 L20.25,9.49870377 C20.6296958,9.49870377 20.943491,9.78085765 20.9931534,10.1469332 L21,10.2487038 C21,10.6283995 20.7178461,10.9421947 20.3517706,10.9918572 L20.25,10.9987038 L13.695179,10.9979536 L13.695179,10.9979536 L13.5970114,10.984476 L13.5970114,10.984476 L13.5016309,10.9582429 L13.5016309,10.9582429 L13.4337826,10.9296888 L13.4337826,10.9296888 C13.3555282,10.8948112 13.2825295,10.8446834 13.2182796,10.7804665 L13.1775595,10.7364313 L13.1775595,10.7364313 L13.1525474,10.7041493 L13.1525474,10.7041493 L13.1050738,10.6355511 L13.1050738,10.6355511 L13.0588237,10.544137 L13.0588237,10.544137 L13.0264601,10.4525511 L13.0264601,10.4525511 L13.0102057,10.3825136 L13.0102057,10.3825136 L13.0002327,10.3015013 L13.0002327,10.3015013 L12.9984732,10.2487038 L12.9984732,3.74481332 C12.9984732,3.33059976 13.3342596,2.99481332 13.7484732,2.99481332 C14.128169,2.99481332 14.4419642,3.2769672 14.4916266,3.64304276 L14.4984732,3.74481332 L14.4978449,8.43869174 Z' fill='#212121'></path>
                                                                </g>
                                                            </g>
                                                        </svg>
                                                    </i>
                                                    End Call
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
              </div>
            </div>
        </div>
    );
};

export default CampaignAgentEditor;
